import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'universal-cookie';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Icon from 'Components/Icon';
import EnvironmentManager from 'Utils/environmentManager';
import { fetchAppStoreData, formatRatingCount } from 'Utils/fetchAppStoreData';

const COOKIE_NAME = 'bilyoner-app-banner-closed';
const COOKIE_DAYS = 7;
const BODY_CLASS = 'smart-app-banner-show';
const STARS = [1, 2, 3, 4, 5];
const cookies = new Cookies();

function SmartAppBanner ({ userAgent }) {
  const [dismissed, setDismissed] = useState(!!cookies.get(COOKIE_NAME));
  const [appStoreData, setAppStoreData] = useState(null);
  const fallbackTimerRef = useRef(null);
  const visibilityListenerRef = useRef(null);

  const osName = userAgent?.os?.name;
  const isIOS = osName === 'iOS';
  const isAndroid = osName === 'Android';
  const isVisible = !dismissed && (isIOS || isAndroid);

  useEffect(() => {
    if (!isVisible) return;
    let cancelled = false;
    fetchAppStoreData().then(data => {
      if (!cancelled && data) setAppStoreData(data);
    });
    return () => {
      cancelled = true;
    };
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      document.body.classList.add(BODY_CLASS);
    } else {
      document.body.classList.remove(BODY_CLASS);
    }
    return () => document.body.classList.remove(BODY_CLASS);
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
      if (visibilityListenerRef.current) {
        document.removeEventListener('visibilitychange', visibilityListenerRef.current);
      }
    };
  }, []);

  if (!isVisible) return null;

  const url = isIOS
    ? EnvironmentManager.getMessageText({ key: 'ios_bottom_smart_banner_url' }) || 'https://app.adjust.com/21ep2tp9'
    : EnvironmentManager.getMessageText({ key: 'and_bottom_smart_banner_url' }) || 'https://app.adjust.com/21s95tji';

  const handleClose = () => {
    const expires = new Date();
    expires.setDate(expires.getDate() + COOKIE_DAYS);
    cookies.set(COOKIE_NAME, true, { path: '/', expires });
    setDismissed(true);
  };

  const cleanupDeepLink = () => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    if (visibilityListenerRef.current) {
      document.removeEventListener('visibilitychange', visibilityListenerRef.current);
      visibilityListenerRef.current = null;
    }
  };

  const handleOpenApp = e => {
    e.preventDefault();

    cleanupDeepLink();

    const storeUrl = url;
    window.location.href = 'bilyoner://';

    const onVisibilityChange = () => {
      if (document.hidden) {
        cleanupDeepLink();
      }
    };

    visibilityListenerRef.current = onVisibilityChange;
    document.addEventListener('visibilitychange', onVisibilityChange);

    fallbackTimerRef.current = setTimeout(() => {
      cleanupDeepLink();
      if (!document.hidden) {
        window.location.href = storeUrl;
      }
    }, 1500);
  };

  const fallbackRating = parseFloat(
    EnvironmentManager.getMessageText({ key: 'bottom_smart_banner_point' }) || '4.8'
  ) || 4.8;
  const fallbackRatingComment = EnvironmentManager.getMessageText({ key: 'bottom_smart_banner_comment' }) || '(50K+)';
  const rating = appStoreData?.rating ?? fallbackRating;
  const ratingCountDisplay = appStoreData
    ? `(${ formatRatingCount(appStoreData.ratingCount) })`
    : fallbackRatingComment;

  const activeStarCount = Math.round(rating);

  return (
    <div className="smart-app-banner">
      <button className="smart-app-banner__close" onClick={ handleClose } aria-label="Kapat">
        ×
      </button>
      <div className="smart-app-banner__icon">
        <Icon
          iconName="svgSmartBannerBilyonerIcon"
          svgViewBox="0 0 524.1 524.1"
        />
      </div>
      <div className="smart-app-banner__info">
        <span className="smart-app-banner__title">Bilyoner</span>
        <div className="smart-app-banner__meta">
          <span className="smart-app-banner__rating">{ rating.toFixed(1) }</span>
          <div className="smart-app-banner__stars">
            { STARS.map(star => (
              <Icon
                key={ star }
                iconName="svgStar"
                className={ `smart-app-banner__star${ star > activeStarCount ? ' smart-app-banner__star--passive' : '' }` }
              />
            )) }
          </div>
          <span className="smart-app-banner__download-count">
            { ratingCountDisplay }
          </span>
        </div>
      </div>
      <a
        href={ url }
        className="smart-app-banner__cta"
        onClick={ handleOpenApp }
      >
        Uygulamada Aç
      </a>
    </div>
  );
}

SmartAppBanner.propTypes = {
  userAgent: PropTypes.object,
};

const mapStateToProps = state => ({ userAgent: state.userAgent });
export default connect(mapStateToProps)(SmartAppBanner);
